import { IonCard, IonCardContent, IonImg } from '@ionic/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  AnnouncementDto,
  AnnouncementsApi,
} from '../../services/AnnouncementsApi.service';
import { AnnouncementFeedSkeleton } from '../dashboard/HomepageSkeletons';
import { openExternalLink } from '../../utils/openExternalLink';
import { MyCratesWidget } from '../crates/widgets/CrateWidgets';
import './AnnouncementWidget.scss';

export const AnnouncementWidget: React.FC<{
  editing: boolean;
  limit?: number;
  fallback?: boolean;
}> = ({ editing, limit, fallback = true }) => {
  const [items, setItems] = useState<AnnouncementDto[]>([]);
  const [loading, setLoading] = useState(true);
  const history = useHistory();

  useEffect(() => {
    AnnouncementsApi.listActive()
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const content = useMemo(() => {
    if (loading) {
      const skeletonCount = typeof limit === 'number' ? limit : 3;
      return (
        <IonCard className="modern-widget-card announcement-widget">
          <IonCardContent className="modern-widget-content">
            <div className="widget-header">
              <h3 className="widget-title">New and Upcoming</h3>
            </div>
            <AnnouncementFeedSkeleton count={skeletonCount} />
          </IonCardContent>
        </IonCard>
      );
    }

    const list = Array.isArray(items) ? items : [];
    const shown = typeof limit === 'number' ? list.slice(0, limit) : list;
    if (list.length === 0) {
      return fallback ? (
        <MyCratesWidget editing={editing} numShown={7} />
      ) : null;
    }

    const isYouTubeUrl = (url: string): boolean => {
      try {
        const u = new URL(url);
        return (
          u.hostname.includes('youtube.com') || u.hostname.includes('youtu.be')
        );
      } catch {
        return false;
      }
    };

    const toYouTubeEmbedUrl = (url: string): string => {
      try {
        const u = new URL(url);
        let id = '';
        if (u.hostname.includes('youtu.be')) {
          id = u.pathname.replace('/', '');
        } else if (u.pathname.includes('/watch')) {
          id = u.searchParams.get('v') || '';
        } else if (u.pathname.includes('/embed/')) {
          id = u.pathname.split('/embed/')[1] || '';
        }
        return id ? `https://www.youtube.com/embed/${id}` : url;
      } catch {
        return url;
      }
    };

    const ensureAbsoluteUrl = (url: string): string => {
      if (!url) return '';
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      if (url.startsWith('//')) return 'https:' + url;
      if (/^[\w.-]+\.[a-z]{2,}/i.test(url)) return `https://${url}`;
      return url;
    };

    const renderMedia = (a: AnnouncementDto) => {
      if (!a.mediaUrl) return null;
      const mediaUrl = ensureAbsoluteUrl(a.mediaUrl);
      if (a.mediaType === 'video' && isYouTubeUrl(a.mediaUrl)) {
        const embed = toYouTubeEmbedUrl(mediaUrl);
        return (
          <div className="announcement-embed">
            <iframe
              src={embed}
              title={a.title || 'Announcement video'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        );
      }
      const media =
        a.mediaType === 'video' ? (
          <video
            className="announcement-media"
            controls={!editing}
            muted
            playsInline
            preload="metadata"
          >
            <source src={mediaUrl} />
          </video>
        ) : (
          <IonImg className="announcement-media" src={mediaUrl} />
        );
      return media;
    };

    const formatDate = (iso?: string) => {
      if (!iso) return '';
      const d = new Date(iso);
      try {
        return d.toLocaleString([], {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      } catch {
        return d.toDateString();
      }
    };

    const navigateTo = (url: string) => {
      if (!url) return;
      if (url.startsWith('/')) {
        history.push(url);
      } else {
        openExternalLink(url);
      }
    };

    return (
      <IonCard className="modern-widget-card announcement-widget">
        <IonCardContent className="modern-widget-content">
          <div className="widget-header">
            <h3 className="widget-title">New and Upcoming</h3>
          </div>
          <div className="announcement-scroll">
            {shown.map((a) => {
              const clickable = !!a.linkUrl && !editing;
              const hasMedia = !!a.mediaUrl && a.mediaType === 'image';

              return (
                <div
                  key={a.id}
                  className={`feed-item${a.highlighted ? ' highlighted' : ''}${clickable ? ' clickable' : ''}`}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={() => clickable && navigateTo(a.linkUrl!)}
                  onKeyDown={(e) => {
                    if (!clickable) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigateTo(a.linkUrl!);
                    }
                  }}
                >
                  {/* Thumbnail */}
                  {hasMedia ? (
                    <div className="feed-thumbnail">
                      <img
                        src={ensureAbsoluteUrl(a.mediaUrl!)}
                        alt={a.title || 'Announcement'}
                      />
                    </div>
                  ) : (
                    <div
                      className="feed-thumbnail feed-thumbnail-empty"
                      aria-hidden="true"
                    >
                      <span>{a.title}</span>
                    </div>
                  )}

                  {(a.title || a.startAt) && (
                    <div className="feed-meta">
                      {a.startAt && (
                        <span className="feed-date">
                          {formatDate(a.startAt)}
                        </span>
                      )}
                      {a.title && (
                        <span className="feed-title" title={a.title}>
                          {a.title}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </IonCardContent>
      </IonCard>
    );
  }, [items, loading, editing, limit, fallback]);

  return <>{content}</>;
};

export default AnnouncementWidget;
